// libraries
import clsx from 'clsx'

// components
import Section from '/react4xp/components/Section'
import RoundedBox from '/react4xp/components/RoundedBox'
import Image from '/react4xp/components/Image'
import Link from '/react4xp/components/Link'
import Breadcrumbs from '/react4xp/components/Breadcrumbs'

// utils
import { firstChar, tel, mailto } from '/react4xp/utils/functions'

// types
export interface EmployeeCardProps {
    className?: string
    isAnimated?: boolean
    color?: 'green-light' | 'beige'
    image?: {
        src?: string
        alt?: string
    }
    title?: string
    position?: string
    location?: string
    tag?: {
        label?: string
        href?: string
        external?: boolean
    }
    tag2?: {
        label?: string
        href?: string
        external?: boolean
    }
    phone?: {
        label?: string
        phone?: string
    }
    email?: {
        label?: string
        email?: string
    }
    map?: {
        label?: string
        href?: string
        external?: boolean
    }
    linkedin?: {
        label?: string
        href?: string
        external?: boolean
    }
    researchgate?: {
        label?: string
        href?: string
        external?: boolean
    }
    fullProfile?: {
        label?: string
        href?: string
        external?: boolean
    }
    breadcrumbs?: {
        label: string
        url?: string
    }[]
}

// css
import './index.scss'

export default function EmployeeCard({
    className,
    isAnimated,
    color,
    image,
    title,
    position,
    location,
    tag,
    tag2,
    phone,
    email,
    map,
    linkedin,
    researchgate,
    fullProfile,
    breadcrumbs
}: EmployeeCardProps) {
    return (
        <Section>
            <div
                className='container'
                data-stagger-up={isAnimated ? 'true' : 'false'}
            >
                {breadcrumbs && (
                    <Breadcrumbs
                        className='employee-card-component__breadcrumbs'
                        items={breadcrumbs}
                    />
                )}
                <RoundedBox
                    className={clsx(
                        'employee-card-component',
                        className
                    )}
                    bgColor={color}
                >
                    <div className='employee-card-component__flex'>

                        <div className='employee-card-component__image'>
                            {image?.src ? (
                                <Image
                                    src={{
                                        src: image.src || '',
                                        width: 350,
                                        height: 350
                                    }}
                                    alt={image.alt}
                                    sizes='(max-width: 350px) 100vw, 350px'
                                    lazyLoad
                                    className='cover'
                                />
                            ) : (
                                <p className='uppercase h1'>
                                    {firstChar(title || '')}
                                </p>
                            )}
                        </div>

                        <div className='employee-card-component__content'>

                            {title && (
                                <h1 className='h1'>
                                    {title}
                                </h1>
                            )}

                            {(position || location) && (    
                                <div className='employee-card-component__info'>

                                    {position && (
                                        <p className='lead-paragraph'>
                                            {position}
                                        </p>
                                    )}

                                    {location && (
                                        <p className='body'>
                                            {location}
                                        </p>
                                    )}
                                    
                                </div>
                            )}

                            {(tag?.label || tag2?.label) && (
                                <div className='employee-card-component__tags'>
                                    {tag?.label && (
                                        <a
                                            href={tag.href}
                                            className='employee-card-component__tag'
                                            target={tag.external ? '_blank' : undefined}
                                            rel={tag.external ? 'noopener noreferrer' : undefined}
                                        >
                                            {tag.label}
                                        </a>
                                    )}
                                    
                                    {tag2?.label && (
                                        <a
                                            href={tag2.href}
                                            className='employee-card-component__tag'
                                            target={tag2.external ? '_blank' : undefined}
                                            rel={tag2.external ? 'noopener noreferrer' : undefined}
                                        >
                                            {tag2.label}
                                        </a>
                                    )}
                                </div>
                            )}

                            {(phone || email || map || linkedin || researchgate) && (
                                <div className='employee-card-component__links'>

                                    {phone?.phone && (
                                        <Link
                                            label={{
                                                noUnderline: phone.label,
                                                underline: phone.phone
                                            }}
                                            icon={{
                                                type: 'phone',
                                                iconFirst: true
                                            }}
                                            href={tel(phone.phone || '')}
                                            className='body'
                                        />
                                    )}

                                    {email?.email && (
                                        <Link
                                            label={{
                                                underline: email.label
                                            }}
                                            icon={{
                                                type: 'envelope',
                                                iconFirst: true
                                            }}
                                            href={mailto(email.email || '')}
                                            className='body'
                                        />
                                    )}

                                    {map?.href && (
                                        <Link
                                            label={{
                                                underline: map.label
                                            }}
                                            icon={{
                                                type: 'map-pin',
                                                iconFirst: true
                                            }}
                                            href={map.href}
                                            isExternal={true}
                                            className='body'
                                        />
                                    )}

                                    {linkedin?.href && (
                                        <Link
                                            label={{
                                                underline: linkedin.label
                                            }}
                                            icon={{
                                                type: 'linkedin',
                                                iconFirst: true
                                            }}
                                            href={linkedin.href}
                                            isExternal={true}
                                            className='body'
                                        />
                                    )}

                                    {researchgate?.href && (
                                        <Link
                                            label={{
                                                underline: researchgate.label
                                            }}
                                            icon={{
                                                type: 'researchgate',
                                                iconFirst: true
                                            }}
                                            href={researchgate.href}
                                            isExternal={true}
                                            className='body'
                                        />
                                    )}

                                </div>
                            )}

                            {fullProfile?.href && (
                                <div className='employee-card-component__profile'>
                                    <a
                                        className='button-black'
                                        target={fullProfile.external ? '_blank' : undefined}
                                        href={fullProfile.href}>
                                        {fullProfile.label}
                                    </a>
                                </div>
                            )}

                        </div>

                    </div>
                </RoundedBox>
            </div>
        </Section>
    )
}
